import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Pressable, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { SideNavMessageIcon } from '../../atoms/icons/SideNavMessageIcon'
import { SecondaryButton, SecondaryButtonProps } from '../../atoms/secondaryButton/SecondaryButton'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const MessagingIconContainer = styled.View<{ theme: ThemeType; hasCustomTrigger: boolean }>(
  ({ theme: { spacing }, hasCustomTrigger }) => ({
    zIndex: 'auto',
    position: 'relative',
    ...(!hasCustomTrigger && {
      top: '-4px',
      right: spacing['12px'],
    }),
  }),
)

export type MessageHubProps = {
  messagingPopover: React.FC<{
    closePopover: () => void
    className?: string
    messagesPopoverContainer?: React.RefObject<HTMLElement>
    selectedEpisodeId?: string
  }>
  forceMessagingHubToOpenPopover: boolean
  onMessagesPopoverOpen: () => void
  numUnreadMessages: number
  selectedEpisodeId?: string
  triggerStyle?: ViewStyle
  triggerText?: React.ReactNode
  triggerTextColor?: SecondaryButtonProps['customTextColor']
  triggerTextSize?: SecondaryButtonProps['customTextSize']
  highlightIconWhenOpen?: boolean
  tooltipContent?: React.ReactNode
  tooltipOffset?: number
  closeTooltipOnScroll?: boolean
}

export const MessagingHub: FunctionComponent<MessageHubProps> = ({
  messagingPopover: PopoverComponent,
  forceMessagingHubToOpenPopover,
  onMessagesPopoverOpen,
  numUnreadMessages,
  selectedEpisodeId,
  triggerStyle,
  triggerText,
  triggerTextColor,
  triggerTextSize,
  highlightIconWhenOpen = false,
  tooltipContent,
  tooltipOffset,
  closeTooltipOnScroll = false,
}) => {
  const [showMessagingPopover, setShowMessagingPopover] = useState(false)
  const messagesPopoverContainer = useRef(null)
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  useEffect(() => {
    if (forceMessagingHubToOpenPopover) {
      setShowMessagingPopover(true)
    }
  }, [forceMessagingHubToOpenPopover])

  useEffect(() => {
    if (showMessagingPopover) {
      onMessagesPopoverOpen()
    }
  }, [onMessagesPopoverOpen, showMessagingPopover])

  const handleClosePopover = useCallback(() => {
    setShowMessagingPopover(false)
  }, [])

  const messageIcon = (
    <SideNavMessageIcon
      fillColor={showMessagingPopover && highlightIconWhenOpen ? colors.iconActive : colors.iconDefault}
      unreadMessageCount={numUnreadMessages}
    />
  )
  const accessibilityLabel = formatMessage({
    defaultMessage: 'Message client',
    description: 'accessibility label for messaging button in Activity tab',
  })
  const onTriggerPress = () => setShowMessagingPopover((showMessagingPopover) => !showMessagingPopover)

  const trigger = triggerText ? (
    <SecondaryButton
      testID={tID('MessagesPopover-MessageIcon')}
      accessibilityLabel={accessibilityLabel}
      onPress={onTriggerPress}
      style={triggerStyle}
      text={triggerText}
      customTextColor={triggerTextColor}
      customTextSize={triggerTextSize}
      leftIcon={messageIcon}
      iconColor={showMessagingPopover && highlightIconWhenOpen ? colors.iconActive : colors.iconDefault}
    />
  ) : (
    <Pressable
      testID={tID('MessagesPopover-MessageIcon')}
      accessibilityLabel={accessibilityLabel}
      onPress={onTriggerPress}
      style={triggerStyle}
    >
      {messageIcon}
    </Pressable>
  )

  return (
    <MessagingIconContainer ref={messagesPopoverContainer} hasCustomTrigger={!!triggerStyle}>
      {tooltipContent ? (
        <Tooltip
          asChild
          hoverEnabled
          placement='bottom'
          content={tooltipContent}
          offset={tooltipOffset}
          hideTooltip={showMessagingPopover}
          closeOnScroll={closeTooltipOnScroll}
        >
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      {showMessagingPopover && (
        <PopoverComponent
          closePopover={handleClosePopover}
          className='messaging-hub-popover'
          messagesPopoverContainer={messagesPopoverContainer}
          selectedEpisodeId={selectedEpisodeId}
        />
      )}
    </MessagingIconContainer>
  )
}
